import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injectable,
  provideAppInitializer,
  provideZoneChangeDetection,
  isDevMode,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  provideRouter,
  TitleStrategy,
  UrlSerializer,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { IntlService, CldrIntlService } from '@progress/kendo-angular-intl';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { APP_ROUTES } from './app.routes';
import { credentialsInterceptor } from '@core/auth/interceptors/credentials.interceptor';
import { tenantInterceptor } from '@core/auth/interceptors/tenant.interceptor';
import { authErrorInterceptor } from '@core/auth/interceptors/auth-error.interceptor';
import { forbiddenInterceptor } from '@core/auth/interceptors/forbidden.interceptor';
import { kioskTokenInterceptor } from '@modules/kiosk-device/kiosk-token.interceptor';
import { httpActivityInterceptor } from '@core/interceptors/http-activity.interceptor';
import { httpLoggingInterceptor } from '@core/interceptors/http-logging.interceptor';
import { BusyClickGuardService } from '@core/services/busy-click-guard.service';
import { TouchTooltipService } from '@core/services/touch-tooltip.service';
import { AuthService } from '@core/auth/services/auth.service';
import { GlobalErrorHandler } from '@core/services/global-error-handler';
import { EncryptedUrlSerializer } from '@core/services/encrypted-url-serializer';
import { AppTitleStrategy } from '@core/services/app-title.strategy';
import { provideServiceWorker } from '@angular/service-worker';
import { providePdfViewer } from '@shared/components/pdf-viewer';
import { provideMediaViewer } from '@shared/components/media-viewer';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { TOOLTIP_CONFIG } from '@shared/constants';

@Injectable()
class MondayFirstIntlService extends CldrIntlService {
  override firstDay(): number {
    return 1;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    NgbTooltipConfig,
    provideEnvironmentInitializer(() => {
      const tooltip = inject(NgbTooltipConfig);
      tooltip.triggers = TOOLTIP_CONFIG.triggers;
      tooltip.container = TOOLTIP_CONFIG.container;
      tooltip.openDelay = TOOLTIP_CONFIG.openDelay;
      tooltip.closeDelay = TOOLTIP_CONFIG.closeDelay;
      tooltip.animation = TOOLTIP_CONFIG.animation;
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withViewTransitions()
    ),
    provideAnimations(),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        httpActivityInterceptor,
        httpLoggingInterceptor,
        kioskTokenInterceptor,
        credentialsInterceptor,
        tenantInterceptor,
        authErrorInterceptor,
        forbiddenInterceptor,
      ])
    ),
    provideAppInitializer(() => inject(AuthService).loadSession()),
    // App-wide double-click guard for every button: a click that starts a write holds that button until
    // the write settles, so no action can be submitted twice. Nothing to wire per component.
    provideAppInitializer(() => inject(BusyClickGuardService).install()),
    // App-wide touch fix for every tooltip: a tapped hint dismisses itself instead of sitting on the
    // page until the user taps elsewhere (touch devices fire the emulated `mouseenter` but no
    // `mouseleave`). Nothing to wire per component.
    provideAppInitializer(() => inject(TouchTooltipService).install()),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: UrlSerializer, useClass: EncryptedUrlSerializer },
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    importProvidersFrom(DialogModule),
    { provide: IntlService, useClass: MondayFirstIntlService },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    providePdfViewer({
      defaultZoom: 100,
      minZoom: 50,
      maxZoom: 500,
      fitMode: 'auto',
      theme: 'light',
    }),
    provideMediaViewer({
      view: 'grid',
      pageSize: 24,
      minZoom: 25,
      maxZoom: 500,
      fitMode: 'contain',
      theme: 'light',
    }),
  ],
};
